import { createContext, type RefObject } from "react";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import { defaultError } from "@-/com.npmjs.react-context-default-error";

export const ActivePrimaryNavigationDestinationNameContext = createContext(
  defaultError<string | null>("ActivePrimaryNavigationDestinationNameContext"),
);

/**
 * Imperative mirror of the navigator's COMMITTED active tab. Unlike the render-value context above,
 * this stable ref is readable from a subtree frozen by react-native-screens, so inactive screens can
 * synchronously reject database invalidations and refetch triggers without waiting for a render.
 *
 * Deliberately nullable rather than `defaultError`: only native's `PrimaryNavigationLayout` provides it. On
 * web the app shell is a plain `<Slot />` with no bottom-tab navigator, so Browse legitimately
 * renders outside any provider; readers treat `null` as "no bottom-tab boundary" and fall back to
 * plain navigation-tree focus.
 */
export const CommittedPrimaryNavigationDestinationNameRefContext = createContext<RefObject<
  string | null
> | null>(null);

/**
 * Worklet-readable mirror of {@link ActivePrimaryNavigationDestinationNameContext}. Consumers that drive
 * UI-thread animations (e.g. tab icon scale) should read this instead of the JS context so
 * the animation target stays consistent under JS-thread jank.
 */
export const ActivePrimaryNavigationDestinationNameSharedValueContext = createContext(
  defaultError<SharedValue<string | null>>(
    "ActivePrimaryNavigationDestinationNameSharedValueContext",
  ),
);

/**
 * The tab the user has TAPPED but whose scene the navigator has not committed focus to yet —
 * i.e. the window between the optimistic promotion in the tabPress listener and the navigator
 * settling on that route. `null` once focus is committed (or the tap was a re-tap of the
 * already-focused tab, which never enters a pending window).
 *
 * The primary navigation strip icons read this to paint a "loading" treatment (shrink + shimmer) that
 * resolves back to the normal active state the moment the destination actually lands.
 */
export const PendingPrimaryNavigationDestinationNameContext = createContext<string | null>(null);
